import { useMemo } from 'react';

import type { EnhancePanelAction } from '@modules/_shared/components/image-gallery/enhance/enhance-image-types';
import { useLocale } from '@shared/hooks/locale';

const PRESERVE_IDENTITY =
  'Preserve exact facial features, skin tone, clothing, and identity.';
const PRESERVE_FACE_ONLY =
  'Preserve exact facial features, skin tone, and identity.';
const SHARP_EYES =
  'Sharp focus on the eyes, natural skin texture with visible pores.';

const shot = (mm: number, fstop: number) => `Shot at ${mm}mm f/${fstop}.`;

const BG_ONLY =
  "Change only the background and depth of field; keep the subject's face, hair, pose, expression, and clothing exactly as in the reference. ";

const PASTEL_COLORS = [
  'soft lavender',
  'blush pink',
  'mint green',
  'baby blue',
  'peach',
  'pale lilac',
  'soft coral',
  'dusty rose',
  'light sage',
  'powder blue',
  'warm vanilla',
  'pale mauve',
  'soft apricot',
  'light periwinkle',
  'pale aqua',
  'soft buttercup yellow',
  'light rosewood',
  'pale teal',
];

const getRandomPastelPrompt = () => {
  const color = PASTEL_COLORS[Math.floor(Math.random() * PASTEL_COLORS.length)];
  return `${BG_ONLY}Professional headshot of the same person from the reference image. ${PRESERVE_IDENTITY} Background is a smooth, seamless ${color} pastel studio backdrop with soft even lighting. Gentle gradient from slightly lighter at center to slightly deeper at edges. Natural edge blending between subject and background. Soft diffused lighting from front-left at 45 degrees. ${SHARP_EYES} ${shot(
    85,
    4,
  )}`;
};

export const useEnhanceProfileImageActions = () => {
  const { t } = useLocale();

  return useMemo(() => {
    const categories = [
      { id: 'all', label: t('All'), emoji: '✦' },
      { id: 'quality', label: t('Quality'), emoji: '✨' },
      { id: 'background', label: t('Background'), emoji: '🖼️' },
      { id: 'professional', label: t('Professional'), emoji: '👔' },
      { id: 'cleanup', label: t('Cleanup'), emoji: '🧹' },
    ];

    const actions: EnhancePanelAction[] = [
      // ── Quality ──
      {
        id: 'profile-quality-studio',
        label: t('Studio quality'),
        emoji: '✨',
        description: t('Professional studio-grade portrait'),
        category: 'quality',
        prompt: `Professional corporate headshot of the same person from the reference image. ${PRESERVE_FACE_ONLY} Soft diffused lighting from front-left at 45 degrees with subtle fill light from right, creating gentle shadows for depth. ${SHARP_EYES} Soft micro-contrast. ${shot(
          85,
          2.8,
        )}, shallow depth of field. Clean neutral background. The result should look like professional real estate agent photography with natural retouching only.`,
      },
      {
        id: 'profile-quality-polished',
        label: t('Polished portrait'),
        emoji: '📸',
        description: t('Refined, natural professional look'),
        category: 'quality',
        prompt: `Polished professional headshot of the same person from the reference image. ${PRESERVE_FACE_ONLY} Soft, natural lighting from front-left at 45 degrees with gentle fill, balanced exposure and true-to-life colors. Light, natural retouching — even skin tone with texture preserved and slightly brighter eyes. Clean, lightly blurred neutral background. ${SHARP_EYES} ${shot(
          85,
          3.5,
        )}, moderate depth of field. The result should look more refined than a quick profile photo but not heavily studio-styled — a believable, photographic professional portrait.`,
      },

      // ── Background: Studio / Abstract ──
      {
        id: 'profile-bg-clean',
        label: t('Clean background'),
        emoji: '🖼️',
        description: t('Blur or clean up the background'),
        category: 'background',
        prompt: `${BG_ONLY}Professional headshot of the same person from the reference image. ${PRESERVE_IDENTITY} Apply a natural shallow depth of field with smooth, professional bokeh on the background, removing distracting elements. Subject remains perfectly sharp with crisp focus on the eyes, natural skin texture with visible pores. ${shot(
          85,
          2,
        )}, natural transition between subject and background. A trustworthy real estate agency website portrait.`,
      },
      {
        id: 'profile-bg-neutral',
        label: t('Neutral background'),
        emoji: '⬜',
        description: t('Professional neutral studio backdrop'),
        category: 'background',
        prompt: `${BG_ONLY}Professional corporate headshot of the same person from the reference image. ${PRESERVE_IDENTITY} Background is a clean, professional neutral studio backdrop — soft gradient from light gray to slightly darker gray. Natural edge blending between subject and background. Soft diffused lighting from front-left at 45 degrees, subtle fill light from right. ${SHARP_EYES} ${shot(
          85,
          4,
        )}`,
      },
      {
        id: 'profile-bg-white',
        label: t('White studio'),
        emoji: '⚪',
        description: t('Pure white, high-key studio lighting'),
        category: 'background',
        prompt: `${BG_ONLY}Professional headshot of the same person from the reference image. ${PRESERVE_IDENTITY} Background is pure white, high-key studio lighting with no visible shadows on the backdrop. Bright, even illumination from multiple softboxes. Natural edge separation between subject and white background. ${SHARP_EYES} ${shot(
          85,
          4,
        )}`,
      },
      {
        id: 'profile-bg-dark',
        label: t('Dark studio'),
        emoji: '⚫',
        description: t('Deep charcoal, dramatic low-key lighting'),
        category: 'background',
        prompt: `${BG_ONLY}Professional headshot of the same person from the reference image. ${PRESERVE_IDENTITY} Background is deep charcoal to black, dramatic low-key studio lighting. Strong key light from 45 degrees left creating cinematic depth. Subtle rim light on the opposite side to separate subject from dark background. ${SHARP_EYES} ${shot(
          85,
          2.8,
        )}`,
      },
      {
        id: 'profile-bg-warm-gradient',
        label: t('Warm gradient'),
        emoji: '🟠',
        description: t('Soft beige-to-cream gradient'),
        category: 'background',
        prompt: `${BG_ONLY}Professional headshot of the same person from the reference image. ${PRESERVE_IDENTITY} Background is a smooth warm gradient from soft beige at center to light cream at edges. Warm, flattering studio lighting with soft diffusion. Natural edge blending between subject and background. ${SHARP_EYES} ${shot(
          85,
          4,
        )}`,
      },
      {
        id: 'profile-bg-cool-gradient',
        label: t('Cool gradient'),
        emoji: '🔵',
        description: t('Soft blue-gray to white gradient'),
        category: 'background',
        prompt: `${BG_ONLY}Professional headshot of the same person from the reference image. ${PRESERVE_IDENTITY} Background is a smooth cool gradient from soft blue-gray at center to near-white at edges. Clean, modern studio lighting with neutral color temperature. Natural edge blending between subject and background. ${SHARP_EYES} ${shot(
          85,
          4,
        )}`,
      },
      {
        id: 'profile-bg-pastel-random',
        label: t('Random pastel'),
        emoji: '🎨',
        description: t('Unique pastel color each time'),
        category: 'background',
        prompt: '',
        getPrompt: getRandomPastelPrompt,
      },

      // ── Background: Environment ──
      {
        id: 'profile-bg-modern-office',
        label: t('Modern office'),
        emoji: '🏢',
        description: t('Glass-walled office with soft natural light'),
        category: 'background',
        prompt: `${BG_ONLY}Professional headshot of the same person from the reference image. ${PRESERVE_IDENTITY} Background is a softly blurred modern office interior with glass walls, clean desks, and warm natural light filtering through large windows. Shallow depth of field, professional bokeh. Soft directional lighting from front-left at 45 degrees. ${SHARP_EYES} ${shot(
          85,
          2,
        )}`,
      },
      {
        id: 'profile-bg-luxury-lobby',
        label: t('Luxury lobby'),
        emoji: '🏨',
        description: t('Elegant marble lobby with warm ambient light'),
        category: 'background',
        prompt: `${BG_ONLY}Professional headshot of the same person from the reference image. ${PRESERVE_IDENTITY} Background is a softly blurred luxury hotel or building lobby with marble surfaces, warm ambient lighting, and elegant architectural details. Shallow depth of field. Soft warm directional lighting from front-left. ${SHARP_EYES} ${shot(
          85,
          2,
        )}`,
      },
      {
        id: 'profile-bg-city-skyline',
        label: t('City skyline'),
        emoji: '🌆',
        description: t('Urban skyline through floor-to-ceiling windows'),
        category: 'background',
        prompt: `${BG_ONLY}Professional headshot of the same person from the reference image. ${PRESERVE_IDENTITY} Background is a softly blurred city skyline visible through floor-to-ceiling windows, with natural daylight and soft blue tones. Shallow depth of field, urban bokeh. Soft directional lighting from front. ${SHARP_EYES} ${shot(
          85,
          2,
        )}`,
      },
      {
        id: 'profile-bg-garden',
        label: t('Garden terrace'),
        emoji: '🌿',
        description: t('Lush green outdoor terrace, soft daylight'),
        category: 'background',
        prompt: `${BG_ONLY}Professional headshot of the same person from the reference image. ${PRESERVE_IDENTITY} Background is a softly blurred lush garden terrace with green foliage, potted plants, and warm natural sunlight filtering through leaves. Shallow depth of field, natural green bokeh. Soft directional lighting. ${SHARP_EYES} ${shot(
          85,
          2,
        )}`,
      },
      {
        id: 'profile-bg-exposed-brick',
        label: t('Exposed brick'),
        emoji: '🧱',
        description: t('Warm industrial brick wall'),
        category: 'background',
        prompt: `${BG_ONLY}Professional headshot of the same person from the reference image. ${PRESERVE_IDENTITY} Background is a warm-toned exposed brick wall with subtle texture, slightly out of focus. Warm studio lighting with soft directional key light from front-left at 45 degrees. ${SHARP_EYES} ${shot(
          85,
          2.8,
        )}`,
      },
      {
        id: 'profile-bg-bookshelf',
        label: t('Bookshelf'),
        emoji: '📚',
        description: t('Blurred bookshelf wall, intellectual warmth'),
        category: 'background',
        prompt: `${BG_ONLY}Professional headshot of the same person from the reference image. ${PRESERVE_IDENTITY} Background is a softly blurred bookshelf wall filled with books, creating warm intellectual ambiance with rich wood tones. Shallow depth of field. Warm directional lighting from front-left. ${SHARP_EYES} ${shot(
          85,
          2,
        )}`,
      },
      {
        id: 'profile-bg-park',
        label: t('Park'),
        emoji: '🌳',
        description: t('Soft-focus park with trees and natural light'),
        category: 'background',
        prompt: `${BG_ONLY}Professional outdoor headshot of the same person from the reference image. ${PRESERVE_IDENTITY} Background is a softly blurred park setting with trees, dappled sunlight, and green foliage. Natural golden-hour warmth. Shallow depth of field, smooth natural bokeh. Soft directional lighting. ${SHARP_EYES} ${shot(
          85,
          2,
        )}`,
      },

      // ── Professional ──
      {
        id: 'profile-pro-corporate',
        label: t('Corporate headshot'),
        emoji: '👔',
        description: t('Polished executive portrait style'),
        category: 'professional',
        prompt: `Professional corporate headshot of the same person from the reference image. ${PRESERVE_FACE_ONLY} Style: classic real estate executive portrait. Soft key light from 45 degrees above left, gentle fill light from right, creating subtle shadows for depth. Background is minimal, blurred, and neutral-toned. Clothing looks crisp and professional. ${SHARP_EYES} Expression is confident, calm, and approachable — a trustworthy real estate advisor. ${shot(
          85,
          2.8,
        )}, shallow depth of field.`,
      },
      {
        id: 'profile-pro-warm',
        label: t('Warm & approachable'),
        emoji: '🌅',
        description: t('Warm tones, friendly appearance'),
        category: 'professional',
        prompt: `Professional headshot of the same person from the reference image. ${PRESERVE_FACE_ONLY} Apply warm, inviting color temperature with subtle golden warmth to skin tones for a healthy, friendly appearance. Soft diffused lighting, slightly warmer than neutral. Eyes are bright and engaging. Expression is warm, personable, and approachable — the kind of real estate agent clients want to meet. ${shot(
          85,
          2.8,
        )}, shallow depth of field, natural skin texture with visible pores. The look should stay believable and photographic.`,
      },
      {
        id: 'profile-pro-linkedin',
        label: t('LinkedIn-ready'),
        emoji: '💼',
        description: t('Optimized for LinkedIn profile'),
        category: 'professional',
        prompt: `Professional LinkedIn-optimized headshot of the same person from the reference image. ${PRESERVE_FACE_ONLY} Clean, minimal neutral background (soft blue-gray gradient). Bright, even studio lighting from front at 45 degrees that renders well at small thumbnail sizes. Subject framed from chest up, centered. Clothing looks crisp and professional. Expression is confident, approachable, and trustworthy — a top-performing real estate agent. Sharp focus on the eyes, vivid but natural colors, natural skin texture with visible pores. ${shot(
          85,
          4,
        )}`,
      },
      {
        id: 'profile-pro-agent',
        label: t('Real estate agent'),
        emoji: '🏠',
        description: t('Agent portrait with property context'),
        category: 'professional',
        prompt: `${BG_ONLY}Professional real estate agent headshot of the same person from the reference image. ${PRESERVE_FACE_ONLY} Subject dressed in smart business attire, looking trustworthy, knowledgeable, and welcoming. Background is a softly blurred upscale property interior — elegant living room or modern building entrance with warm ambient light visible through windows. Soft directional lighting with warm tones from 45 degrees. ${shot(
          85,
          2,
        )}, shallow depth of field, sharp focus on the eyes, natural skin texture with visible pores. Expression is confident and professional.`,
      },

      // ── Cleanup ──
      {
        id: 'profile-cleanup-lighting',
        label: t('Fix lighting'),
        emoji: '💡',
        description: t('Even out harsh shadows on face'),
        category: 'cleanup',
        prompt:
          'Enhance this headshot for professional use. Preserve exact facial features, skin tone, and identity from the reference image. Soften harsh shadows while maintaining dimension. Even out skin tone subtly with natural texture preserved. Brighten eyes slightly. Balance the overall exposure so the face is evenly and flatteringly lit. Maintain natural lighting direction but reduce extreme contrast. Sharp focus on the eyes, natural skin with visible pores. The result should look like skilled photography, not heavy retouching.',
      },
      {
        id: 'profile-cleanup-smile',
        label: t('Smile enhance'),
        emoji: '😊',
        description: t('Subtle, natural smile improvement'),
        category: 'cleanup',
        prompt:
          'Expression-only edit on the attached photo: gently shift the face toward a warm, natural smile — slightly lifted corners of the mouth and a relaxed, approachable look. Do not change identity, do not replace the image with a new portrait, and do not change background, clothing, hair, lighting direction, shadows, camera angle, crop, or any part of the scene outside the minimal facial-muscle changes needed for the smile. Keep the same person, same pose, same everything else; the result must look like this exact photo with only a subtle smile adjustment.',
      },
      {
        id: 'profile-face-bright-eyes',
        label: t('Bright eyes'),
        emoji: '✨',
        description: t('Catch lights and clearer iris detail'),
        category: 'cleanup',
        prompt:
          'Professional headshot enhancement of the same person from the reference image. Preserve exact facial structure, eye shape, iris color, and identity — do not change how the person looks. Add subtle, natural catch lights in the eyes and slightly increase clarity of the iris and sclera (whites of eyes) while keeping them realistic. Sharp focus on the eyes. No enlargement or reshaping of eyes. Natural skin texture with visible pores elsewhere. Soft diffused lighting from front-left at 45 degrees.',
      },
    ];

    return { categories, subcategories: [], actions };
  }, [t]);
};
